import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { MessageSchema } from './primitives.js';
const c = initContract();
/**
 * INV-H5 / IT-S8 — ATTACHMENTS (bug-triage plan, Oct 2026).
 *
 * One generic table, one panel, mounted under a document or a master record:
 * a scanned purchase bill under the invoice it was keyed from, a photo on an
 * item, a signed contract on a customer. The attachment belongs to ONE
 * entity, named by `(entityKind, entityId)`; the kind is a closed list so the
 * server can map it to the entity's own table (does it exist, is it ours) and
 * to the entity's own permissions.
 *
 * ── PERMISSION FOLLOWS THE ENTITY ─────────────────────────────────────────
 *
 * There is no `attachments.*` permission and must not be: PART 16.2.2's grid
 * is a closed 12x5 and a thirteenth area is a spec change. Seeing a file
 * follows the entity's VIEW; adding or removing one follows its EDIT — the
 * map below is the one definition, read by the API's guard and by the panel
 * when it decides whether to draw the upload button.
 *
 * ── STORAGE ───────────────────────────────────────────────────────────────
 *
 * DECISION.md §10.6 settles on Cloudflare R2 (S3-compatible) for customer
 * files. The API's `platform/object-storage.ts` is that client, switched on
 * by the OBJECT_STORAGE_* environment group: set, a new file's bytes go to
 * the bucket under `tenants/<tenant_id>/attachments/<id>`; unset (the shipped
 * default until the bucket exists), they live in the `attachments` row itself
 * (`bytea`). Either way two caps keep a Postgres backup from quietly becoming
 * a file server's: 10 MB per file and 200 MB per company. The contract does
 * not know which store a row uses — `download` streams the bytes through the
 * API in both cases, so the permission check and the Content-Disposition
 * headers stay on the one response the browser sees.
 */
export const ATTACHMENT_ENTITY_KINDS = [
    'invoice',
    'item',
    'partner',
    'quotation',
    'sales_order',
    'credit_note',
    'purchase_invoice',
    'purchase_order',
    'goods_receipt',
    'project',
    // Wave 2 of specs/erp-gap-analysis.md ("attachments everywhere"): the three
    // transactional records that had an editor and no files panel. The CHECK on
    // `attachments.entity_kind` (20261027100000) and `ENTITY_TABLES` in the API
    // widen with this list, or the panel offers an upload the row refuses.
    'delivery_note',
    'purchase_return',
    'import_order',
];
export const AttachmentEntityKindSchema = z.enum(ATTACHMENT_ENTITY_KINDS);
/** Per file. The multipart parser refuses above it before a byte is stored. */
export const ATTACHMENT_MAX_FILE_BYTES = 10 * 1024 * 1024;
/** Per company, summed over every attachment it holds. */
export const ATTACHMENT_TENANT_QUOTA_BYTES = 200 * 1024 * 1024;
/**
 * The kind → permission map. `view` opens the list and the bytes; `edit`
 * adds and removes. One definition for the guard and the panel.
 */
export const ATTACHMENT_PERMISSIONS = {
    invoice: { view: 'sales.view', edit: 'sales.edit' },
    quotation: { view: 'sales.view', edit: 'sales.edit' },
    sales_order: { view: 'sales.view', edit: 'sales.edit' },
    credit_note: { view: 'sales.view', edit: 'sales.edit' },
    purchase_invoice: { view: 'purchasing.view', edit: 'purchasing.edit' },
    purchase_order: { view: 'purchasing.view', edit: 'purchasing.edit' },
    goods_receipt: { view: 'purchasing.view', edit: 'purchasing.edit' },
    delivery_note: { view: 'sales.view', edit: 'sales.edit' },
    purchase_return: { view: 'purchasing.view', edit: 'purchasing.edit' },
    import_order: { view: 'purchasing.view', edit: 'purchasing.edit' },
    item: { view: 'inventory.view', edit: 'inventory.edit' },
    partner: { view: 'crm.view', edit: 'crm.edit' },
    project: { view: 'projects.view', edit: 'projects.edit' },
};
export const AttachmentSchema = z
    .object({
    id: z.uuid(),
    entityKind: AttachmentEntityKindSchema,
    entityId: z.uuid(),
    fileName: z.string(),
    contentType: z.string(),
    byteSize: z.number().int().nonnegative(),
    /** NULL once the uploader's account is gone; the row outlives the person. */
    uploadedBy: z.uuid().nullable(),
    uploadedByName: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
/**
 * The list carries the company's usage beside the rows, so the panel can say
 * how much room is left without a second request. Every write answers with
 * the whole list, so the panel never merges.
 */
export const AttachmentListResponseSchema = z
    .object({
    attachments: z.array(AttachmentSchema),
    usedBytes: z.number().int().nonnegative(),
    quotaBytes: z.number().int().positive(),
})
    .strict();
const EntityParamsSchema = z
    .object({ kind: AttachmentEntityKindSchema, entityId: z.uuid() })
    .strict();
export const attachmentsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/attachments/:kind/:entityId',
        pathParams: EntityParamsSchema,
        responses: {
            200: AttachmentListResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'INV-H5: the files on one record. Needs the record’s own view permission.',
    },
    upload: {
        method: 'POST',
        path: '/api/attachments/:kind/:entityId',
        pathParams: EntityParamsSchema,
        contentType: 'multipart/form-data',
        body: c.type(),
        responses: {
            201: AttachmentListResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            /** The file is over ATTACHMENT_MAX_FILE_BYTES. */
            413: MessageSchema,
            /** The company is at ATTACHMENT_TENANT_QUOTA_BYTES. */
            507: MessageSchema,
        },
        summary: 'INV-H5: attach one file to a record. Needs the record’s own edit permission; 10 MB per file, 200 MB per company.',
    },
    download: {
        method: 'GET',
        path: '/api/attachments/:id/content',
        pathParams: z.object({ id: z.uuid() }).strict(),
        responses: {
            // The bytes, under the STORED content type and a Content-Disposition
            // carrying the stored file name. Deliberately not `c.otherResponse`:
            // that stamps ONE static content type over the reply after the
            // handler has set the row's own (an image would come back as
            // octet-stream and no thumbnail would draw). A plain unknown body
            // leaves the header the controller set, and the body is not JSON.
            200: z.unknown(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'INV-H5: the file itself. Checked against the owning record’s view permission.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/attachments/:id',
        pathParams: z.object({ id: z.uuid() }).strict(),
        body: z.object({}).strict(),
        responses: {
            200: AttachmentListResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'INV-H5: remove a file. Needs the owning record’s edit permission; answers with what is left.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=attachments.js.map